import { useState, useRef, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useWarningsData } from 'Modules/devices/.hooks/useWarningsData';
import { useIntl } from "react-intl";
import { NavLink } from "react-router-dom";
import './styles.scss';

//components
import VidfleetConfiguration from "Modules/vidfleet/.components/configuration";
import ConfigurationUploadMode from "Modules/vidfleet/.components/configurationUploadMode";
import ConfigurationHubSpot from "Modules/vidfleet/.components/configurationHubSpot";
import { Tooltip, Select } from "@gpstrackit/react-controls";
import { Chip, Grid, IconButton, Icon, Badge } from "@mui/material";
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import ChipStatus from 'Components/ChipStatus';
import InfoIcon from '@mui/icons-material/Info';
import ProgressBar from "Components/ProgressBar";
import { TagsGroup } from 'Components/Tag';

//Notification
import { NotificationManager } from "Components/Notifications";

//Views
import BulkPairing from 'Modules/devices/.components/bulkPairing';
import AssignFirmware from 'Modules/devices/.components/assignFirmware';
import ActivateSimCard from 'Modules/devices/.components/activateSimCard';
import BulkUnpairing from 'Modules/devices/.components/bulkUnpairing';
import VidfleetInformation from 'Modules/.common/.components/vidfleetInformation';
import ChangeSimProvider from 'Modules/devices/.components/changeSimProvider';
import EditSwapSimCard from 'Modules/devices/.components/editSwapSimCard';
import UpdateFeature from 'Modules/devices/.components/featureTab/.components/updateFeature';
import ChangeStatus from 'Modules/devices/.components/changeStatus';
import SendCommand from 'Modules/devices/.components/sendCommand';
import ChangeScript from 'Modules/devices/.components/changeScript';
import ChangePulsCustomer from 'Modules/devices/.components/changePulsCustomer';
import MoveToOtherAccount from 'Modules/devices/.components/moveToOtherAccount';
import MarkAsRma from 'Modules/devices/.components/markAsRma';
import Details from 'Modules/devices/views/main/.components/Details';
import BulkActions from "Modules/devices/.components/bulkActions";
import ChangeTimezone from 'Modules/devices/.components/changeTimezone';
import RebootDevice from 'Modules/devices/.components/rebootDevice';
import CreateDiagnosticDevice from 'Modules/devices/.components/createDiagnosticDevice';
import CoverageDetails from 'Modules/devices/.components/coverageDetails'
import ReprocessOdoHours from 'Modules/accounts/.components/reprocessOdoHours';
import MaintenanceWarnings from 'Modules/devices/.components/maintenanceWarnings';
import VidFleetOverview from "Modules/devices/.components/vidFleetVideo";
import Activities from "Modules/activities/.components/tab";
import BulkTags from 'Modules/tags/.components/bulkTags';
import AssignBillingAccount from "Modules/dealers/views/.components/assignBillingAccount";

//Actions
import {
    vidfleetConfigurationBulkOpenModal,
    vidfleetConfigurationUploadModeLoadData,
    vidfleetConfigurationHubSpotLoadData,
    assignFirmwareOpenModal,
    activateCameraSimCardOpenModal,
    bulkUnpairingOpenModal,
    vidfleetInformationOpenModal,
    changeSimProviderOpenModal,
    editSwapSimCardOpenModal,
    bulkPairingOpenModal,
    sendCommandOpenModal,
    featureTabOpenModal,
    changeStatusOpenModal,
    changeScriptOpenModal,
    changePulsCustomerOpenModal,
    searchEntityRequest,
    moveToOtherAccountOpenModal,
    markAsRmaOpenModal,
    deviceDetailShow,
    bulkActionsOpenModal,
    changeTimezoneOpenModal,
    rebootDeviceOpenModal,
    createDiagnosticDeviceOpenModal,
    coverageDetailsOpenModal,
    reprocessOdoHoursOpenModal,
    openModalMaintenanceWarnings,
    vidFleetVideoOpenModal,
    clearRedux,
    activitiesSearchOpenModal,
    bulkTagsOpenModal,
    openModalAssignBillingAccount
} from 'Redux/actions';

//Helpers
import { getDateFormatted, getBadget, getStatusOptions, getWarningCategory, getTagColor } from 'Core/data/Helpers';

import { HasPermission } from "Core/security"

// Stable selector — defined outside the component so useSelector only re-renders on items changes,
// not on every entity state update (loading, totalCount, etc.)
const EMPTY_ITEMS = [];
const selectDevicesTabItems = state =>
    state.commonISRedux?.entities?.['devices-account']?.items ?? EMPTY_ITEMS;

const DevicesTab = (props) => {
    const { companyId, tab, height, company } = props;
    const { messages } = useIntl();
    const tableRef = useRef();
    const dispatch = useDispatch();

    const statusOptions = [
        {
            id: 1,
            label: messages['active']
        },
        {
            id: 4,
            label: messages['cancelled']
        },
        {
            id: 3,
            label: messages['involuntary_suspension']
        },
        {
            id: 2,
            label: messages['voluntary_suspension']
        }
    ]

    const openModalVidFleetConfiguration = useSelector(state => state.vidfleetConfigurationRedux.openModal);
    const configurationUploadModeOpenModal = useSelector(state => state.vidfleetConfigurationUploadModeRedux.openModal)
    const configurationHubSpotOpenModal = useSelector(state => state.vidfleetConfigurationHubSpotRedux.openModal)
    const bulkPOpenModal = useSelector(state => state.bulkPairingRedux.openModal);
    const assignFirmOpenModal = useSelector(state => state.assignFirmwareDeviceRedux.openModal);
    const activeSimCardOpenModal = useSelector(state => state.activateCameraSimCardRedux.openModal);
    const bulkUnOpenModal = useSelector(state => state.bulkUnpairingRedux.openModal);
    const deviceInfoOpenModal = useSelector(state => state.deviceInfoRedux.openModal);
    const changeSimPOpenModal = useSelector(state => state.changeSimProviderRedux.openModal);
    const editSwapSimCardOModal = useSelector(state => state.editSwapSimCardRedux.openModal);
    const editDeviceOpenModal = useSelector(state => state.featureTabUpdateFeatureRedux.openModal);
    const changeStatusOModal = useSelector(state => state.changeStatusRedux.openModal);
    const sendCommandsOpenModal = useSelector(state => state.sendCommandRedux.openModal);
    const massSMS = useSelector(state => state.sendCommandRedux.massSMS);
    const cgPulsCustomerOpenModal = useSelector(state => state.changePulsCustomerRedux.openModal);
    const cgScriptOpenModal = useSelector(state => state.changeScriptRedux.openModal);
    const firmwares = useSelector(state => state.generalRedux.firmwares);
    const { refreshResult } = useSelector(state => state.deviceOverviewRedux);
    const openModalMoveToOtherAccount = useSelector(state => state.moveToOtherAccountRedux.openModal)
    const openModalMarkAsRma = useSelector(state => state.markAsRmaRedux.openModal);
    const deviceDetailsOpenModal = useSelector(state => state.deviceDetailsRedux.openModal);
    const user = useSelector(state => state.securityRedux.user);
    const bulkAOpenModal = useSelector(state => state.bulkActionsRedux.openModal);
    const changeTimezoneOModal = useSelector(state => state.changeTimezoneRedux.openModal);
    const rebootDeOpenModal = useSelector(state => state.rebootDeviceRedux.openModal);
    const openModalCoverageDetails = useSelector(state => state.coverageDetailsRedux.openModal);
    const openModalCreateDiagnosticDevice = useSelector(state => state.createDiagnosticDeviceRedux.openModal);
    const openModalReprocessOdoHoursDevices = useSelector(state => state.reprocessOdoHoursRedux.openModal);
    const openModalViewMaintenanceWarnings = useSelector(state => state.maintenanceWarningsRedux.openModal);
    //Scheduled Actions
    const deleteScheduledResult = useSelector(state => state.deleteScheduledActionRedux.result);
    const editScheduledActionResult = useSelector(state => state.editScheduledActionRedux.result);
    const changeStatusByUnitResult = useSelector(state => state.changeStatusRedux.schedule);
    const lang = useSelector(state => state.commonRedux.language);
    const openModalVidFleetVideo = useSelector(state => state.vidFleetVideoRedux.openModal);
    const openModalActivities = useSelector(state => state.activitiesTabRedux.openModal);
    const openModalBulkTags = useSelector(state => state.bulkTagsRedux.openModal);
    const assignBillingAccountOpenModal = useSelector(state => state.assignBillingAccountRedux.openModal);
    const devicesTabItems = useSelector(selectDevicesTabItems);

    const { warningsMap, fetchWarningsForIds, resetWarnings } = useWarningsData();

    useEffect(() => {
        const ids = devicesTabItems.map(d => d.id).filter(Boolean);
        if (ids.length > 0) fetchWarningsForIds(ids);
    }, [devicesTabItems, fetchWarningsForIds]);

    const [selectedItems, setSelectedItems] = useState([]);
    const [columns, setColumns] = useState([
        {
            key: 'view',
            show: true,
            locked: true,
            title: "",
            width: 40,
            icon: true,
            render: (item) => {
                return (
                    <Tooltip title={messages['devices_main_show_last_reading_info']}>
                        <Icon style={{ cursor: "pointer", color: '#1D2734' }} className="toolbar-list-icon fs-20" onClick={() => {
                            dispatch(deviceDetailShow(item.id))
                        }}>
                            remove_red_eye
                        </Icon>
                    </Tooltip>
                )
            }
        },
        {
            key: 'laveStraming',
            show: true,
            locked: true,
            title: "",
            width: 40,
            icon: true,
            position: 1,
            render: (item) => {
                if (item?.deviceModel?.manufacturer?.name?.toUpperCase() === "Vidfleet".toUpperCase())
                    return (
                        <Tooltip title={messages['devices_main_live_streaming']}>
                            <Icon style={{ cursor: "pointer", color: '#1D2734' }} className="toolbar-list-icon fs-20" onClick={() => {
                                dispatch(vidFleetVideoOpenModal({
                                    messages: messages, //For to access to language keys
                                    mode: "video",
                                    type: "streaming", //Depends of the playing type, the url VidFleet API changes
                                    id: item?.esn, //This could be a ESN or Clip Id
                                    autoplay: true, //If is true the video will be played automatically
                                    deviceModel: item?.deviceModel?.name?.includes("360") ? '360' : '4k', //4k|360
                                    disabled: false,
                                    timezone: item?.timezone
                                }));
                            }}>
                                video_call
                            </Icon>
                        </Tooltip>
                    )
            }
        },
        {
            key: 'activities',
            show: true,
            locked: true,
            title: "",
            width: 40,
            icon: true,
            position: 2,
            render: (item) => {
                return (
                    <Tooltip title={messages['users_tab_show_activities']}>
                        <Icon style={{ cursor: "pointer" }} className="toolbar-list-icon fs-20" onClick={() => {
                            if (HasPermission(user, "act")) {
                                dispatch(activitiesSearchOpenModal({ entityName: "Unit", entityId: item.id }, "devices-tab-modal"))
                            } else {
                                NotificationManager.warning(
                                    messages['common_error_permission'],
                                    messages['users_tab_users'],
                                    5000,
                                );
                            }
                        }} >
                            assignment
                        </Icon>
                    </Tooltip>
                )
            }
        },
        {
            key: "id",
            field: "id",
            title: messages['devices_tab_id'],
            show: true,
            resize: true,
            width: 150,
            export: true,
            sort: true,
            functionString: 'return row?.id',
            code: "devices_tab_id",
            render: (item) => {
                return <span>{item?.id}</span>
            }
        },
        {
            key: "label",
            field: "label",
            title: messages['devices_tab_label'],
            show: true,
            resize: true,
            width: 250,
            sort: true,
            export: true,
            functionString: 'return row?.label',
            code: "devices_tab_label",
            render: (item) => {
                return (
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        className="context-menu-container"
                        wrap="nowrap"
                    >
                        <Grid item>
                            <NavLink to={`/app/devices/overview/${item?.id}`} style={{ color: 'black' }}>
                                <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.label}</span>
                            </NavLink>
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: "status",
            title: messages['devices_tab_status'],
            show: true,
            resize: true,
            sort: true,
            width: 200,
            export: true,
            functionString: 'return `${row?.status === 0 ? "Desactivated" : row?.status === 1 ? "Active": row?.status === 2 ? "Voluntary Suspension" : row?.status === 3 ? "Involuntary Suspension" : row?.status === 4 ? "Cancelled" : row?.status === 6 ? "RMA" : row?.status === 20 ? "Inactive" : "Unknown" }`',
            code: "devices_tab_status",
            render: (item) => {
                let nextScheduledAction = item?.nextScheduledAction
                let message = null
                if (nextScheduledAction) {
                    try {
                        let data = JSON.parse(nextScheduledAction)
                        if (data && data?.action === "statusUpdate") {
                            message = `${messages['accounts_main_next_scheduled_action']}: ${data?.scheduleOn}`
                            let status = statusOptions.find(item => item?.id === data?.data?.newStatusId)
                            message = `${messages[data?.action] || data?.action}${status ? ` ${messages[status?.label] || status?.label}` : ''} ${messages['accounts_main_at']} ${data?.scheduleOn}`
                        }
                    } catch (exc) { }
                }
                return (
                    <div style={{ display: 'flex', gap: 8 }}>
                        <ChipStatus statusCode={item?.status} messages={messages} width="auto" />
                        {nextScheduledAction &&
                            <div style={{ marginLeft: 5 }}>
                                <Tooltip title={message}>
                                    <Icon style={{ color: '#1664f5', cursor: 'pointer' }}>access_alarms</Icon>
                                </Tooltip>
                            </div>
                        }
                    </div>
                )
            }
        },
        {
            key: "reference",
            field: "reference",
            title: messages['devices_tab_reference'],
            show: true,
            resize: true,
            width: 250,
            export: true,
            sort: true,
            functionString: 'return row?.reference',
            code: "devices_tab_reference",
            render: (item) => {
                return <span>{item?.reference}</span>
            }
        },
        {
            key: "deviceModel_manufacturer_name",
            title: messages['devices_tab_manufacturer'],
            show: true,
            resize: true,
            width: 250,
            export: true,
            sort: true,
            functionString: 'return row?.deviceModel?.manufacturer?.name',
            code: "devices_tab_manufacturer",
            render: (item) => {
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: 5 }}>{item?.deviceModel?.manufacturer?.name}</span>
                        {
                            item?.deviceModel?.manufacturer?.name?.toUpperCase() === "Vidfleet".toUpperCase() &&
                            <Tooltip
                                title={messages['vidfleet_show_vidfleet_info']}
                            >
                                <IconButton
                                    style={{ padding: 0, color: '#444444' }}
                                    onClick={() => dispatch(vidfleetInformationOpenModal(item.esn, item.id, item?.timezone, item?.companyId))}
                                    size="large">
                                    <InfoIcon />
                                </IconButton>
                            </Tooltip>
                        }
                    </div>
                );
            }
        },
        {
            key: "deviceModel_name",
            title: messages['devices_tab_model'],
            show: true,
            resize: true,
            width: 250,
            export: true,
            sort: true,
            functionString: 'return row?.deviceModel?.name',
            code: "devices_tab_model",
            render: (item) => {
                return <span>{item?.deviceModel?.name}</span>
            }
        },
        {
            key: 'Group',
            field: "unitConfig.pulsGroup",
            show: true,
            locked: false,
            title: messages["devices_main_puls_group"],
            width: 200,
            resize: true,
            export: true,
            functionString: 'return row?.unitConfig?.pulsGroup',
            code: "devices_tab_puls_group",
            render: (item) => {
                return <span>
                    {item?.unitConfig?.pulsGroup || ""}
                </span>
            }
        },
        {
            key: "esn",
            title: messages['devices_tab_serial'],
            show: true,
            resize: true,
            sort: true,
            width: 250,
            export: true,
            functionString: 'return row?.esn',
            code: "devices_tab_serial",
            render: (item) => {
                return (
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        className="context-menu-container"
                        wrap="nowrap"
                    >
                        <Grid item>
                            <NavLink to={`/app/devices/overview/${item?.id}`} style={{ color: 'black' }}>
                                <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.esn}</span>
                            </NavLink>
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: "imei",
            field: "imei",
            title: messages['devices_tab_imei'],
            show: true,
            resize: true,
            sort: true,
            width: 250,
            export: true,
            functionString: 'return row?.imei',
            code: "devices_tab_imei",
            render: (item) => {
                return (
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        className="context-menu-container"
                        wrap="nowrap"
                    >
                        <Grid item>
                            <NavLink to={`/app/devices/overview/${item?.id}`} style={{ color: 'black' }}>
                                <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.imei}</span>
                            </NavLink>
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: "linkedUnitIdLabel",
            field: "linkedUnit { id label }",
            title: messages['devices_tab_linked_device_label'],
            width: 200,
            show: false,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.linkedUnit?.label',
            code: "devices_tab_linked_device_label",
            render: (item) => {
                return (
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        className="context-menu-container"
                        wrap="nowrap"
                    >
                        <Grid item>
                            <NavLink to={`/app/devices/overview/${item?.linkedUnit?.id}`} style={{ color: 'black' }}>
                                <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.linkedUnit?.label}</span>
                            </NavLink>
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: "linkedUnit_esn",
            field: "linkedUnit { id esn }",
            title: messages['devices_tab_linked_device'],
            show: true,
            resize: true,
            sort: true,
            width: 250,
            export: true,
            functionString: 'return row?.linkedUnit?.esn',
            code: "devices_tab_linked_device",
            render: (item) => {
                return (
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        className="context-menu-container"
                        wrap="nowrap"
                    >
                        <Grid item>
                            <NavLink to={`/app/devices/overview/${item?.linkedUnit?.id}`} style={{ color: 'black' }}>
                                <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.linkedUnit?.esn}</span>
                            </NavLink>
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: "linkedUnitBy_label",
            field: "linkedByUnit { id esn }",
            title: messages['devices_tab_linked_camera'],
            show: true,
            resize: true,
            width: 250,
            export: true,
            functionString: 'return row?.linkedByUnit?.esn',
            code: "devices_tab_linked_camera",
            render: (item) => {
                return (
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        className="context-menu-container"
                        wrap="nowrap"
                    >
                        <Grid item>
                            <NavLink to={`/app/devices/overview/${item?.linkedByUnit?.id}`} style={{ color: 'black' }}>
                                <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.linkedByUnit?.esn}</span>
                            </NavLink>
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: "simCard_iccId",
            field: "simCard.iccId",
            title: messages['devices_tab_sim_number'],
            show: true,
            resize: true,
            export: true,
            sort: true,
            functionString: 'return row?.simCard?.iccId',
            code: "devices_tab_sim_number",
            render: (item) => {
                return <span>{item?.simCard?.iccId}</span>
            }
        },
        {
            key: "simCard_msisdn",
            field: "simCard.msisdn",
            title: messages['devices_tab_sim_phone'],
            show: true,
            resize: true,
            export: true,
            sort: true,
            functionString: 'return row?.simCard?.msisdn',
            code: "devices_tab_sim_phone",
            render: (item) => {
                return <span>{item?.simCard?.msisdn}</span>
            }
        },
        {
            key: "network",
            field: "network",
            title: messages['devices_tab_network'],
            show: true,
            resize: true,
            export: true,
            sort: true,
            functionString: 'return row?.network',
            code: "devices_tab_network",
            render: (item) => {
                return <span>{item?.network}</span>
            }
        },
        {
            key: "simCard_simProviderAccount_name",
            field: "simCard.simProviderAccount { name simProvider { name } }",
            title: messages['devices_tab_sim_provider_account'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.simCard?.simProviderAccount?.name',
            code: "devices_tab_sim_provider_account",
            render: (item) => {
                return <span>
                    {item?.simCard?.simProviderAccount?.name || ""}
                </span>
            }
        },
        {
            key: "simCard_simProviderAccount_simProvider_name",
            field: "simCard.simProviderAccount { name simProvider { name } }",
            title: messages['devices_tab_sim_provider'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.simCard?.simProviderAccount?.simProvider?.name',
            code: "devices_tab_sim_provider",
            render: (item) => {
                return <span>
                    {item?.simCard?.simProviderAccount?.simProvider?.name || ""}
                </span>
            }
        },
        {
            key: "lastReading_unitTime",
            field: "lastReading.unitTime",
            title: messages['devices_tab_unit_time'],
            show: true,
            sort: true,
            resize: true,
            width: 250,
            export: true,
            functionString: `
            function formatDate(date,tz) {
                let dateToformat = moment(date)
                let formatted = moment(dateToformat).tz(tz).format("YYYY/MM/DD HH:mm:ss")
                return formatted;
              }
                return row?.lastReading?.unitTime ? formatDate(row?.lastReading?.unitTime,row?.timezone) : ''
            `,
            code: "devices_tab_unit_time",
            render: (item) => {
                return <span>{item?.lastReading?.unitTime && getDateFormatted(item?.lastReading?.unitTime, "date", "datetime", null, item?.timezone)}</span>
            }
        },
        {
            key: "lastReading_serverTime",
            field: "lastReading.serverTime",
            title: messages['devices_tab_server_time'],
            show: true,
            sort: true,
            resize: true,
            width: 250,
            export: true,
            functionString: `
            function formatDate(date,tz) {
                let dateToformat = moment(date)
                let formatted = moment(dateToformat).tz(tz).format("YYYY/MM/DD HH:mm:ss")
                return formatted;
              }
                return row?.lastReading?.serverTime ? formatDate(row?.lastReading?.serverTime,row?.timezone) : ''
            `,
            code: "devices_tab_server_time",
            render: (item) => {
                return <span>{item?.lastReading?.serverTime && getDateFormatted(item?.lastReading?.serverTime, "date", "datetime", null, item?.timezone)}</span>
            }
        },
        {
            key: "lastReading_lastMessageTime",
            title: messages['devices_tab_last_message_time'],
            field: "lastReading.lastMessageTime",
            show: true,
            sort: true,
            resize: true,
            width: 250,
            export: true,
            functionString: `
            function formatDate(date,tz) {
                let dateToformat = moment(date)
                let formatted = moment(dateToformat).tz(tz).format("YYYY/MM/DD HH:mm:ss")
                return formatted;
            }
                return row?.lastReading?.lastMessageTime ? formatDate(row?.lastReading?.lastMessageTime,row?.timezone) : ''
            `,
            code: "devices_tab_last_message_time",
            render: (item) => {
                return <span>{item?.lastReading?.lastMessageTime && getDateFormatted(item?.lastReading?.lastMessageTime, "date", "datetime", null, item?.timezone)}</span>
            }
        },
        {
            key: "age",
            title: messages['devices_tab_age'],
            field: "lastReading.lastMessageTime",
            show: true,
            resize: true,
            width: 200,
            export: true,
            functionString: `
            function fromNowOn(date) {
                let dateToformat = moment(date)
                return dateToformat.fromNow()
              }

              return row?.lastReading?.lastMessageTime ? fromNowOn(row?.lastReading?.lastMessageTime) : ''
            `,
            code: "devices_tab_age",
            render: (item) => {
                return <span>{item?.lastReading?.lastMessageTime && getDateFormatted(item?.lastReading?.lastMessageTime, "date", "age", null, item?.timezone)}</span>
            }
        },
        {
            key: 'Firmware',
            show: false,
            locked: false,
            title: messages["devices_tab_firmware"],
            width: 200,
            resize: true,
            export: true,
            functionString: 'return row?.unitConfig?.firmwareId',
            code: "devices_tab_firmware",
            render: (item) => {

                let currentHardwareVersion = item?.unitConfig?.hardwareVersion
                let firmware = item?.unitConfig?.firmwareId;
                let defaultFirmware = firmwares?.find((x) => x.isDefault && x.hardwareVersion === currentHardwareVersion)
                let currentFirmware = firmwares?.find((x) => x.version === item?.unitConfig?.firmwareId)
                if (firmware && firmware !== "")
                    return (
                        <Grid>
                            <div style={{ display: 'flex' }}>
                                {
                                    _.parseInt(defaultFirmware?.firmwareId) > _.parseInt(currentFirmware?.firmwareId) ?
                                        <div className="d-flex">
                                            <Tooltip title={messages['assign_firmware_outdated']}>
                                                <Chip style={{
                                                    backgroundColor: "#FF0000",
                                                    color: "#ffffff"
                                                }} label={firmware || ''}></Chip>
                                            </Tooltip>
                                        </div>
                                        :
                                        <div className="d-flex">
                                            <Tooltip title={messages['assign_firmware_updated']}>
                                                <Chip style={{
                                                    backgroundColor: "#66BB6A",
                                                    color: "#ffffff"
                                                }} label={firmware || ''}></Chip>
                                            </Tooltip>
                                        </div>
                                }
                            </div>
                        </Grid>
                    )
            }
        },
        {
            key: "unitConfig_hardwareVersion",
            title: messages['devices_main_hardware_version'],
            width: 200,
            show: false,
            locked: false,
            resize: true,
            export: true,
            sort: true,
            functionString: 'return row?.unitConfig?.hardwareVersion',
            code: "devices_main_hardware_version",
            render: (item) => {
                return <span>{item?.unitConfig?.hardwareVersion}</span>
            }
        },
        {
            key: "linkedMode",
            field: "linkedMode",
            title: messages['devices_main_mode'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            sort: true,
            functionString: 'return `${row?.deviceModel?.manufacturer?.name === "VidFleet"? row?.linkedMode ? "Paired Mode" : "Camera as GPS" : ""}`',
            code: "devices_main_mode",
            render: (item) => {
                return <span>
                    {
                        (item?.deviceModel?.manufacturer?.name?.toUpperCase() === "Vidfleet".toUpperCase() || item?.deviceModel?.manufacturer?.name?.toUpperCase() === "SmartWitness".toUpperCase()) &&
                        getBadget(item?.linkedMode ? messages["devices_import_camera_paired_mode"] : messages["devices_import_camera_gps"], item?.linkedMode)
                    }
                </span>
            }
        },
        {
            key: "show",
            field: "show",
            title: messages['devices_main_show_in_web'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            sort: true,
            functionString: 'return `${row?.show ? "Y" : "N" }`',
            code: "devices_main_show_in_web",
            render: (item) => {
                return <span>
                    {
                        item?.show ? "Y" : "N"
                    }
                </span>
            }
        },
        {
            key: "installed",
            field: "installed",
            title: messages['devices_main_installed'],
            sort: true,
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: `
                let lang = "${lang}";
                const translations = {
                    en: {
                        installed: "Installed",
                        no_installed: "Pending Installation",   
                    },
                    es: {
                        installed: "Instalado",
                        no_installed: "Instalación Pendiente",
                    }
                };
                return row?.installed ? translations[lang].installed : translations[lang].no_installed
            `,
            code: "devices_main_installed",
            render: (item) => {
                return <Chip style={{ backgroundColor: item?.installed ? "#66BB6A" : "#5A5A5A", color: "#FFFFFF" }} label={item?.installed ? messages['devices_main_installed'] : messages['devices_main_pending_installed'] || ''}></Chip>
            }
        },
        {
            key: "installedOn",
            field: "installedOn",
            title: messages['devices_main_installed_on'],
            width: 250,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: `
            function formatDate(date,tz) {
                let dateToformat = moment(date)
                let formatted = moment(dateToformat).tz(tz).format("YYYY/MM/DD HH:mm:ss")
                return formatted;
              }
                return row?.installedOn ? formatDate(row?.installedOn,row?.timezone) : ''
            `,
            code: "devices_main_installed_on",
            render: (item) => {
                return <span>{item?.installedOn && getDateFormatted(item?.installedOn, "date", "datetime", null, item?.timezone)}</span>
            }
        },
        {
            key: "unitConfig_coverageScore",
            field: "unitConfig.coverageScore",
            title: messages['devices_main_coverage_score'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            sort: true,
            functionString: 'return row?.unitConfig?.coverageScore',
            code: "devices_main_coverage_score",
            render: (item) => {
                return <span style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => {
                    dispatch(coverageDetailsOpenModal(item.id))
                }}>
                    {
                        `${item?.unitConfig?.coverageScore != null ? `${item?.unitConfig?.coverageScore.toFixed(2)}%` : ""}`
                    }
                </span>
            }
        },
        {
            key: "unitConfig_coverageUpdatedOn",
            field: "unitConfig.coverageUpdatedOn",
            title: messages['devices_main_coverage_updated_on'],
            width: 250,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: `
            function formatDate(date,tz) {
                let dateToformat = moment(date)
                let formatted = moment(dateToformat).tz(tz).format("YYYY/MM/DD HH:mm:ss")
                return formatted;
              }
            return row?.unitConfig?.coverageUpdatedOn ? formatDate(row?.unitConfig?.coverageUpdatedOn,row?.timezone) : ''
            `,
            code: "devices_main_coverage_updated_on",
            render: (item) => {
                return <span>{item?.unitConfig?.coverageUpdatedOn && getDateFormatted(item?.unitConfig?.coverageUpdatedOn, "date", "datetime", null, item?.timezone)}</span>
            }
        },
        {
            key: "unitConfig_healthScore",
            field: "unitConfig.healthScore",
            title: messages['devices_main_maintenance_health_score'],
            width: 250,
            show: true,
            locked: false,
            resize: true,
            export: true,
            sort: true,
            functionString: 'return row?.unitConfig?.healthScore ? row?.unitConfig?.healthScore.toFixed(2) : 0',
            code: "devices_main_maintenance_health_score",
            render: (item) => {
                return <span>{item?.unitConfig?.healthScore ? item?.unitConfig?.healthScore?.toFixed(2) : 0}</span>
            }
        },
        {
            key: "liveMode",
            field: "liveMode",
            title: messages['devices_main_live_mode'],
            width: 250,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.liveMode',
            code: "devices_main_live_mode",
            render: (item) => {
                return <span>{item?.liveMode}</span>
            }
        },
        {
            key: "batteryLevel",
            field: "lastReading.deviceData",
            title: messages['devices_main_battery_level'],
            width: 250,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.lastReading?.deviceData ? JSON.parse(row?.lastReading?.deviceData).batteryPercent ? JSON.parse(row?.lastReading?.deviceData).batteryPercent: "" : ""',
            code: "devices_main_battery_level",
            render: (item) => {
                let battery = item?.lastReading?.deviceData ? JSON.parse(item.lastReading.deviceData).batteryPercent ? JSON.parse(item.lastReading.deviceData).batteryPercent : null : null
                let color = "green"
                if (battery && battery < 10) {
                    color = "red"
                } else if (battery && battery < 20) {
                    color = "yellow"
                }
                return <span>{battery && <ProgressBar value={battery} color={color} />}</span>
            }
        },
        {
            key: "vin",
            field: "vehicleInformation.vin",
            title: messages['devices_main_vin'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.vehicleInformation?.vin',
            code: "devices_main_vin",
            render: (item) => {
                return <span>{item?.vehicleInformation?.vin}</span>
            }
        },
        {
            key: "eventName",
            field: "lastReading.eventName",
            title: messages['devices_main_last_event'],
            width: 250,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.lastReading?.eventName || ""',
            code: "devices_main_last_event",
            render: (item) => {
                return <span>{item?.lastReading?.eventName || ""}</span>
            }
        },
        {
            key: "tags",
            field: "tags",
            title: messages['accounts_main_tags'],
            width: 350,
            show: true,
            locked: false,
            resize: true,
            code: "accounts_main_tags",
            render: (item) => {
                let unitTags = [];
                if (item?.tags && item?.tags?.length) {
                    let tagsItems = JSON.parse(item?.tags);
                    unitTags = tagsItems?.map(t => {
                        return { ...t, color: getTagColor(t?.color) };
                    });
                }

                unitTags = unitTags?.filter(unitTag => unitTag?.id != -1);

                return (
                    <TagsGroup
                        options={unitTags}
                        nameField="label"
                        colorField="color"
                    />
                )
            },
            export: true,
            functionString: 'return row?.tags?.map(x => x.label).join(",\r\n")'
        },
        {
            key: "unitConfig_billingAccount_name",
            field: "unitConfig.billingAccount { id name }", //simCard.simProviderAccount { name simProvider { name } }
            title: messages['devices_main_billing_account'],
            width: 350,
            show: true,
            locked: false,
            resize: true,
            sort: true,
            export: true,
            functionString: 'return row?.unitConfig?.billingAccount?.name || ""',
            code: "devices_main_billing_account",
            render: (item) => {
                return <span>{item?.unitConfig?.billingAccount?.name || ""}</span>
            }
        },
        {
            key: "lastReading_address",
            field: "lastReading.address { address city formatted }",
            title: messages['devices_main_address'],
            width: 250,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.lastReading?.address?.formatted || ""',
            code: "devices_main_address",
            render: (item) => {
                return <span>{item?.lastReading?.address?.formatted || ""}</span>
            }
        },
    ]);

    const SmartWitnessValidation = (items, title) => {
        if (items && items.length > 0) {
            let validateSmartWitness = selectedItems.some((item) => item.deviceModel.manufacturer.name?.toUpperCase() === "SmartWitness".toUpperCase());
            if (validateSmartWitness) {
                NotificationManager.warning(
                    messages['devices_main_smartWitness_error'],
                    title,
                    5000,
                );
                return true
            } else {
                return false
            }
        }
        return false
    }
    const [status, setStatus] = useState(null)

    useEffect(() => {
        if (refreshResult) {
            dispatch(clearRedux("DEVICE_OVERVIEW", { refreshResult: null }))
            if (refreshResult.result) {
                NotificationManager.success(
                    messages['assign_firmware_firmware_cameras_updated'],
                    messages['assign_firmware_title'],
                    5000,
                );
            }
        }
    }, [refreshResult]);

    useEffect(() => {
        (!firmwares || firmwares.length === 0) && dispatch(searchEntityRequest({ key: "firmwares" }));
    }, []);

    useEffect(() => {
        if (deleteScheduledResult?.delete || editScheduledActionResult?.id || changeStatusByUnitResult?.response) {
            resetWarnings();
            tableRef?.current?.getData();
        }
    }, [deleteScheduledResult, editScheduledActionResult, changeStatusByUnitResult, resetWarnings])

    useEffect(() => {
        let currentColumns = columns.map((item) => ({
            ...item,
            title: messages[item.code],
            render: item.key === "status" ? (item) => {
                let nextScheduledAction = item?.nextScheduledAction
                let message = null
                if (nextScheduledAction) {
                    try {
                        let data = JSON.parse(nextScheduledAction)
                        if (data && data?.action === "statusUpdate") {
                            message = `${messages['accounts_main_next_scheduled_action']}: ${data?.scheduleOn}`
                            let status = statusOptions.find(item => item?.id === data?.data?.newStatusId)
                            message = `${messages[data?.action] || data?.action}${status ? ` ${messages[status?.label] || status?.label}` : ''} ${messages['accounts_main_at']} ${data?.scheduleOn}`
                        }
                    } catch (exc) { }
                }
                return (
                    <div style={{ display: 'flex' }}>
                        <ChipStatus statusCode={item?.status} messages={messages} />
                        {nextScheduledAction &&
                            <div style={{ marginLeft: 5 }}>
                                <Tooltip title={message}>
                                    <Icon style={{ color: '#1664f5', cursor: 'pointer' }}>access_alarms</Icon>
                                </Tooltip>
                            </div>
                        }
                    </div>
                )
            } : item.key === "installed" ? (item) => {
                return <Chip style={{ backgroundColor: item?.installed ? "#66BB6A" : "#5A5A5A", color: "#FFFFFF" }} label={item?.installed ? messages['devices_main_installed'] : messages['devices_main_pending_installed'] || ''}></Chip>
            } : item.render,
            functionString: item.key === "installed" ? `
                let lang = "${lang}";
                const translations = {
                    en: {
                        installed: "Installed",
                        no_installed: "Pending Installation",   
                    },
                    es: {
                        installed: "Instalado",
                        no_installed: "Instalación Pendiente",
                    }
                };
                return row?.installed ? translations[lang].installed : translations[lang].no_installed
            ` : item.functionString
        }))
        setColumns(currentColumns)
    }, [lang])

    let innerHeight = window.innerHeight

    // ═══════════════════════════════════════════════════════════════════════════
    // DATAVIEW HANDLERS & CONFIGURATION
    // Extracted from JSX for readability
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Handle condition changes from search/filters
     * Manages tag-based search, status filters, and companyId constraints
     */
    const handleChangeConditions = (tags, conditions, total, newColumns, rows, sort, _p7, fromPreferences) => {
        if (tab === 0) {

            let currentConditions = []
            let currentTags = []
            if (!conditions && tags) {
                currentConditions = tableRef?.current?.conditions;
                currentTags = tags
            } else {
                currentConditions = conditions || [];

                //don't save search bar entered search on prefernces
                currentConditions = currentConditions?.filter((x) => !x?.tags) || []

                // Clear search chips restored from preferences so the tab always starts clean
                if (fromPreferences) tableRef?.current?.setTags?.([]);
            }
            currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'label' && item?.fields[0] !== 'companyId' && item?.fields[0] !== 'status');

            let tagsById = currentTags.filter((item) => typeof item.id === "number");
            if (tagsById?.length > 0) {
                //New Items
                let currentIds = tagsById.map((item) => (item.id));
                if (currentIds.length > 0) {
                    currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
                }
            }
            let tagsByLabel = currentTags.filter((item) => typeof item.id === "string");
            if (tagsByLabel.length > 0) {
                let currentLabels = tagsByLabel.map((item) => (item.label))
                if (currentLabels.length > 0) {
                    currentConditions.push({ fields: ["label", "esn", "imei", "reference", "deviceModel_manufacturer_name", "deviceModel_name", "simCard_msisdn", "simCard_iccId", "unitConfig_hardwareVersion", "unitConfig_billingAccount_name"], terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
                }
            }

            currentConditions.push({ fields: ["companyId"], terms: [companyId], exact: true, conditionOperator: "AND" });

            if (!currentConditions && tags && !deviceDetailsOpenModal) {
                tableRef?.current?.getData({ newConditions: currentConditions, newColumns });
            } else {
                if (!total && !deviceDetailsOpenModal) {
                    tableRef?.current?.getData({ newRows: rows, newSort: sort, newConditions: currentConditions, newColumns });
                }
            }
        }
    };

    /**
     * Handle column changes (reorder, resize, visibility)
     * Updates column titles from messages and preserves custom renderers
     */
    const handleChangeColumns = (items) => {
        if (!deviceDetailsOpenModal) {
            let currentColumns = items.map((item) => ({
                ...item,
                title: item.code ? messages[item.code] : item.title,
                render: item.key === "status" ? (item) => {
                    let nextScheduledAction = item?.nextScheduledAction
                    let message = null
                    if (nextScheduledAction) {
                        try {
                            let data = JSON.parse(nextScheduledAction)
                            if (data && data?.action === "statusUpdate") {
                                message = `${messages['accounts_main_next_scheduled_action']}: ${data?.scheduleOn}`
                                let status = statusOptions.find(item => item?.id === data?.data?.newStatusId)
                                message = `${messages[data?.action] || data?.action}${status ? ` ${messages[status?.label] || status?.label}` : ''} ${messages['accounts_main_at']} ${data?.scheduleOn}`
                            }
                        } catch (exc) { }
                    }
                    return (
                        <div style={{ display: 'flex' }}>
                            <ChipStatus statusCode={item?.status} messages={messages} />
                            {nextScheduledAction &&
                                <div style={{ marginLeft: 5 }}>
                                    <Tooltip title={message}>
                                        <Icon style={{ color: '#1664f5', cursor: 'pointer' }}>access_alarms</Icon>
                                    </Tooltip>
                                </div>
                            }
                        </div>
                    )
                } : item.render,
            }))
            setColumns(currentColumns)
        }
    };

    /**
     * Context menu options for row actions
     * Includes edit, bulk operations, camera actions, change actions, commands, etc.
     */
    const menuOptions = [
        {
            id: "devices_tab_edit",
            label: messages['devices_tab_edit'],
            show: true,
            disabled: !HasPermission(user, "unt", "u"),
            single: true,
            action: () => {
                let validation = false; //SmartWitnessValidation(selectedItems, messages['devices_main_edit']);
                if (!validation) {
                    if (selectedItems[0]?.refurbishedUnit?.refurbishedUnitId) {
                        NotificationManager.warning(
                            messages['device_overview_refurbish_message'],
                            messages['device_overview_title'],
                            3000,
                            null,
                            null,
                            'error'
                        );
                    } else if (selectedItems[0]?.status === 6) {
                        NotificationManager.warning(
                            messages['device_overview_rma_message'],
                            messages['device_overview_title'],
                            3000,
                            null,
                            null,
                            'error'
                        );
                    } else
                        dispatch(featureTabOpenModal(selectedItems[0].id))
                }
            }
        },
        {
            id: "devices_tab_editswap_sim_card",
            label: messages['devices_tab_editswap_sim_card'],
            show: true,
            disabled: !HasPermission(user, "untessc", "u"),
            single: true,
            action: () => {
                let validation = false; //SmartWitnessValidation(selectedItems, messages['devices_tab_editswap_sim_card']);
                if (!validation) {
                    dispatch(editSwapSimCardOpenModal(selectedItems))
                }
            }
        },
        {
            id: "bulk-features",
            label: messages['devices_main_bulk_features'],
            show: true,
            disabled: !HasPermission(user, "untba", "u"),
            multiple: true,
            action: () => {
                if (selectedItems.length > 0) {
                    let manufacturer = selectedItems[0].deviceModel?.manufacturer?.name;
                    let deviceModel = selectedItems[0].deviceModel?.name;
                    let validation = false //SmartWitnessValidation(selectedItems, messages['devices_main_bulk_features'])
                    if (!validation) {
                        let validationMD = selectedItems.some((item) => (item.deviceModel.manufacturer.name !== manufacturer || item.deviceModel.name !== deviceModel));
                        let rma = selectedItems.some((item) => item.status === 6)
                        if (validationMD) {
                            NotificationManager.warning(
                                messages['devices_main_same_manufacturer_device_model'],
                                messages['devices_main_bulk_features'],
                                5000
                            );
                        } else if (rma) {
                            //devices_tab_rma_selected
                            NotificationManager.warning(
                                messages['devices_tab_rma_selected'],
                                messages['devices_main_bulk_features'],
                                5000
                            );
                        } else {
                            dispatch(bulkActionsOpenModal({ devices: selectedItems, bulkFeatures: true }))
                        }
                    }
                } else {
                    NotificationManager.warning(
                        messages['devices_main_select_devices'],
                        messages['devices_main_bulk_features'],
                        5000
                    );
                }
            }
        },
        {
            id: "bulk-tags",
            label: messages['devices_main_bulk_tags'],
            show: true,
            disabled: !HasPermission(user, "assgntgs", "c"),
            multiple: true,
            action: () => {
                if (selectedItems.length > 0) {
                    dispatch(bulkTagsOpenModal(selectedItems))
                } else {
                    NotificationManager.warning(
                        messages['devices_main_select_devices'],
                        messages['devices_main_bulk_tags'],
                        5000
                    );
                }
            }
        },
        {
            id: "devices_tab_assign_unassign_billing_account",
            label: messages['devices_tab_assign_unassign_billing_account'],
            show: true,
            disabled: !HasPermission(user, "assngbllngaccnts", "c"),
            multiple: true,
            action: () => {
                dispatch(openModalAssignBillingAccount({ selectedDevices: selectedItems }))
            }
        },
        {
            id: "create-diagnostic-device",
            label: messages['devices_main_create_diagnostic_device'],
            show: true,
            disabled: !HasPermission(user, "dvdiag", "c"),
            action: () => {
                dispatch(createDiagnosticDeviceOpenModal(selectedItems))
            }
        },
        {
            id: "camera-actions",
            label: messages['devices_main_camera_actions'],
            show: true,
            disabled: !HasPermission(user, "cact"),
            submenu: [
                {
                    id: "bulk-reboot-devices",
                    label: messages['reboot_device_bulk_reboot'],
                    show: true,
                    disabled: false,
                    action: () => {
                        if (selectedItems.length > 0) {
                            let validateSelectedDevices = selectedItems.some((item) => item?.deviceModel?.manufacturer?.name !== "VidFleet");
                            if (validateSelectedDevices) {
                                NotificationManager.warning(
                                    messages["devices_main_only_vidfleet"],
                                    messages['reboot_device_bulk_reboot'],
                                    5000
                                );
                            } else {
                                dispatch(rebootDeviceOpenModal(selectedItems))
                            }
                        } else {
                            dispatch(rebootDeviceOpenModal([]))
                        }
                    }
                },
                {
                    id: "bulk-pairing",
                    label: messages['devices_main_bulk_pairing'],
                    show: true,
                    disabled: !HasPermission(user, "untbp", "u"),
                    action: () => {
                        dispatch(bulkPairingOpenModal());
                    }
                },
                {
                    id: "devices_tab_bulk_vidfleet_Config",
                    label: messages['devices_main_bulk_vidfleet_configuration'],
                    show: true,
                    disabled: !HasPermission(user, "vftcd", "u"),
                    action: () => {
                        //Action
                        if (selectedItems.length > 0) {
                            let first = selectedItems[0];
                            let validateSelectedDevicesV = selectedItems.some((item) => item?.deviceModel.manufacturer?.name !== "VidFleet");
                            let validateDeviceModel = selectedItems.some((item) => item?.deviceModel?.name !== first.deviceModel.name);

                            if (!validateSelectedDevicesV && !validateDeviceModel) {
                                dispatch(vidfleetConfigurationBulkOpenModal());
                            } else {
                                if (validateDeviceModel) {
                                    NotificationManager.warning(
                                        messages["devices_main_bulk_vidfleet_config_device_model"],
                                        messages['devices_tab_bulk_vidfleet_Config'],
                                        5000
                                    );
                                } else {
                                    NotificationManager.warning(
                                        messages["devices_main_only_vidfleet"],
                                        messages['devices_tab_bulk_vidfleet_Config'],
                                        5000
                                    );
                                }

                            }
                        } else {
                            dispatch(vidfleetConfigurationBulkOpenModal());
                        }
                    }
                },
                {
                    id: "devices_tab_bulk_vidfleet_config_upload_mode",
                    label: messages['devices_tab_bulk_vidfleet_config_upload_mode'],
                    show: true,
                    disabled: !HasPermission(user, "vftcdum", "u"),
                    action: () => {
                        //Action
                        if (selectedItems.length > 0) {
                            let validateSelectedDevicesV = selectedItems.some((item) => item?.deviceModel.manufacturer?.name !== "VidFleet");
                            if (!validateSelectedDevicesV) {
                                dispatch(vidfleetConfigurationUploadModeLoadData());
                            } else {
                                NotificationManager.warning(
                                    messages["devices_main_only_vidfleet"],
                                    messages['devices_main_bulk_vidfleet_configuration_upload_mode'],
                                    5000
                                );
                            }
                        } else {
                            dispatch(vidfleetConfigurationUploadModeLoadData());
                        }
                    }
                },
                {
                    id: "devices_tab_bulk_vidfleet_config_hubspot",
                    label: messages['devices_tab_bulk_vidfleet_config_hubspot'],
                    show: true,
                    disabled: !HasPermission(user, "vftunat", "u"),
                    action: () => {
                        //Action
                        if (selectedItems.length > 0) {
                            let validateSelectedDevicesV = selectedItems.some((item) => item?.deviceModel.manufacturer?.name !== "VidFleet");
                            let validateSimProvider = selectedItems.some((item) => item?.simCard?.simProviderAccount?.simProvider?.name !== "AT&T")
                            if (!validateSelectedDevicesV) {
                                if (!validateSimProvider)
                                    dispatch(vidfleetConfigurationHubSpotLoadData());
                                else {
                                    NotificationManager.warning(
                                        messages["devices_main_only_att"],
                                        messages['devices_tab_bulk_vidfleet_config_hubspot'],
                                        5000
                                    );
                                }
                            } else {
                                NotificationManager.warning(
                                    messages["devices_main_only_vidfleet"],
                                    messages['devices_tab_bulk_vidfleet_config_hubspot'],
                                    5000
                                );
                            }
                        } else {
                            dispatch(vidfleetConfigurationHubSpotLoadData());
                        }
                    }
                },
                {
                    id: "devices_tab_assign_firmware",
                    label: messages['devices_tab_assign_firmware'],
                    show: true,
                    disabled: !HasPermission(user, "untaf", "u"),
                    action: () => {
                        let currentSelectedItems = selectedItems;
                        let validateVidfleet = currentSelectedItems.some((item) => item.deviceModel.manufacturer?.name !== "VidFleet");
                        if (!validateVidfleet) {
                            dispatch(assignFirmwareOpenModal(currentSelectedItems))
                        } else {
                            NotificationManager.warning(
                                messages['devices_main_only_vidfleet'],
                                messages['devices_main_assign_firmware'],
                                4000,
                            );
                        }
                    }
                },
                {
                    id: "devices_tab_activate_cameras_sim_card",
                    label: messages['devices_tab_activate_cameras_sim_card'],
                    multiple: true,
                    show: true,
                    disabled: !HasPermission(user, "untasc", "u"),
                    action: () => {
                        let currentSelectedItems = selectedItems
                        let validateVidfleet = currentSelectedItems.some((item) => item.deviceModel.manufacturer?.name !== "VidFleet")
                        if (validateVidfleet) {
                            NotificationManager.warning(
                                messages['devices_main_activate_sim_card_message_error'],
                                messages['devices_main_activate_sim_card'],
                                3000
                            );
                            return
                        }
                        dispatch(activateCameraSimCardOpenModal(currentSelectedItems))
                    }
                },
                {
                    id: "devices_tab_unpair",
                    label: messages['devices_tab_unpair'],
                    show: true,
                    disabled: !HasPermission(user, "untbu", "u"),
                    action: () => {
                        if (selectedItems.length > 0) {
                            let validateVidfleet = selectedItems.some((item) => item.deviceModel.manufacturer.name !== "VidFleet");
                            if (!validateVidfleet) {
                                let validateLinkedUnitId = selectedItems.some((item) => item.linkedUnitId !== null);
                                if (validateLinkedUnitId) {
                                    dispatch(bulkUnpairingOpenModal(selectedItems));
                                } else {
                                    NotificationManager.warning(
                                        messages['devices_bulk_unpairing_devices_mush_have_other_linked_devices'],
                                        messages['devices_bulk_unpairing_unpair'],
                                        5000,
                                    );
                                }
                            } else {
                                NotificationManager.warning(
                                    messages['devices_main_only_vidfleet'],
                                    messages['devices_bulk_unpairing_unpair'],
                                    5000,
                                );
                            }
                        } else dispatch(bulkUnpairingOpenModal([]));
                    }
                },
            ]
        },
        {
            id: "change-actions",
            label: messages['devices_main_change'],
            show: true,
            disabled: !HasPermission(user, "chact"),
            submenu: [
                {
                    id: "change_status",
                    label: messages['devices_tab_change_status'],
                    show: true,
                    disabled: !HasPermission(user, "untcsu", "u"),
                    multiple: true,
                    action: () => {
                        let validation = !HasPermission(user, "chngststsactv", "u") ? selectedItems.some((item) => item.status === 1) : false;
                        let refurbishedValidation = selectedItems.some((item) => item.refurbishedUnit?.refurbishedUnitId)
                        if (refurbishedValidation) {
                            NotificationManager.warning(
                                messages['devices_tab_change_status_warning'],
                                messages['devices_tab_change_status'],
                                5000,
                            );
                            return
                        }
                        if (!validation) {
                            dispatch(changeStatusOpenModal(selectedItems))
                        } else {
                            NotificationManager.warning(
                                messages['devices_tab_change_status_active_message'],
                                messages['devices_tab_change_status'],
                                5000,
                            );
                        }
                    }
                },
                {
                    id: "devices_tab_change_sim_provider",
                    label: messages['change_sim_provider_title'],
                    show: true,
                    disabled: !HasPermission(user, "untcspa", "u"),
                    action: () => {
                        if (selectedItems.length > 0) {
                            let validateGlobalStart = selectedItems.some((item) => item.deviceModel.manufacturer.name !== "Global Star");
                            let validation = false;

                            if (validateGlobalStart) {
                                if (!validation) dispatch(changeSimProviderOpenModal(selectedItems))

                            } else {
                                NotificationManager.warning(
                                    messages['devices_main_global_star_error'],
                                    messages['change_sim_provider_title'],
                                    5000,
                                );
                            }
                        } else {
                            dispatch(changeSimProviderOpenModal([]))
                        }
                    }
                },
                {
                    id: "change-puls-customer",
                    label: messages['change_puls_customer_title'],
                    show: true,
                    disabled: !HasPermission(user, "untcpc", "u"),
                    action: () => {
                        if (selectedItems.length > 0) {

                            let currentCalampApi = selectedItems[0].unitConfig?.calampApi
                            let calampApiValidation = selectedItems.some((item) => item?.unitConfig?.calampApi !== currentCalampApi)
                            if (calampApiValidation) {
                                NotificationManager.warning(
                                    messages['devices_main_warning_message_calamp_api'],
                                    messages['change_puls_customer_title'],
                                    5000,
                                );
                                return
                            }

                            let validateCalamp = selectedItems.some((item) => item.deviceModel.manufacturer.name !== "CalAmp");
                            if (!validateCalamp) {
                                dispatch(changePulsCustomerOpenModal(selectedItems, currentCalampApi))
                            } else {
                                NotificationManager.warning(
                                    messages['devices_main_calamp_error'],
                                    messages['change_puls_customer_title'],
                                    5000,
                                );
                            }
                        } else {
                            dispatch(changePulsCustomerOpenModal([]))
                        }
                    }
                },
                {
                    id: "change-script",
                    label: messages['change_script_title'],
                    show: true,
                    disabled: !HasPermission(user, "untscr", "u"),
                    action: () => {
                        if (selectedItems.length > 0) {
                            let validateCalamp = selectedItems.some((item) => item.deviceModel.manufacturer.name !== "CalAmp");
                            if (!validateCalamp) {
                                let esns = [];
                                selectedItems.map((item, index) => {
                                    esns.push(item.esn);
                                })
                                dispatch(changeScriptOpenModal(esns))
                            } else {
                                NotificationManager.warning(
                                    messages['devices_main_calamp_error'],
                                    messages['change_script_title'],
                                    5000,
                                );
                            }
                        } else {
                            dispatch(changeScriptOpenModal([]))
                        }
                    }
                },
                {
                    id: "change_timezone",
                    label: messages['devices_tab_change_timezone'],
                    show: true,
                    disabled: !HasPermission(user, "untcsu", "u"),
                    multiple: true,
                    action: () => {
                        let validation = false;//SmartWitnessValidation(selectedItems, messages['devices_tab_change_timezone']);
                        let refurbishedValidation = selectedItems.some((item) => item.refurbishedUnit?.refurbishedUnitId)
                        if (refurbishedValidation) {
                            NotificationManager.warning(
                                messages['devices_tab_change_timezone_warning'],
                                messages['devices_tab_change_timezone'],
                                5000,
                            );
                            return
                        }
                        if (!validation) {
                            dispatch(changeTimezoneOpenModal(selectedItems))
                        }
                    }
                },
            ]
        },
        {
            id: "Commands-actions",
            label: messages['devices_main_commands'],
            show: true,
            disabled: !HasPermission(user, "coact"),
            submenu: [
                {
                    id: "send-command",
                    label: messages['devices_main_send_command'],
                    show: true,
                    disabled: !HasPermission(user, "sndcom", "u"),
                    action: () => {
                        let validation = SmartWitnessValidation(selectedItems, messages['devices_main_edit_swap_sim_card'])
                        if (!validation) {
                            if (selectedItems.length > 0) {
                                let manufacturer = selectedItems[0].deviceModel.manufacturer?.name.toUpperCase();
                                let validateVidfleet = selectedItems.some((item) => item.deviceModel.manufacturer?.name.toUpperCase() !== "VidFleet".toUpperCase());
                                if (validateVidfleet) {
                                    let manufacturerValidation = selectedItems.some((item) => item.deviceModel.manufacturer.name.toUpperCase() !== manufacturer);
                                    if (manufacturerValidation) {
                                        NotificationManager.warning(
                                            messages['devices_main_the_manufacturer_of_the_devices_must_be_the_same'],
                                            messages['devices_main_send_command'],
                                            5000
                                        );
                                    } else dispatch(sendCommandOpenModal({ selectedDevices: selectedItems }));
                                } else {
                                    NotificationManager.warning(
                                        messages['devices_main_the_manufacturer_of_the_devices_must_be_different_from_vidfleet'],
                                        messages['devices_main_send_command'],
                                        5000
                                    );
                                }
                            } else dispatch(sendCommandOpenModal());
                        }
                    }
                },
            ]
        },
        {
            id: "devices_tab_move_to_another_account",
            label: messages['devices_tab_move_to_another_account'],
            show: true,
            disabled: !HasPermission(user, "untmaa", "u"),
            action: () => {
                dispatch(moveToOtherAccountOpenModal(selectedItems));
            }
        },
        {
            id: "devices_tab_mark_as_rma",
            label: messages['devices_tab_mark_as_rma'],
            show: true,
            disabled: !HasPermission(user, "untrma", "u"),
            action: () => {
                let validateRMA = selectedItems.some((item) => item.status === 6);
                let validateRefurbish = selectedItems.some((item) => item?.refurbishedUnit?.refurbishedUnitId);
                if (validateRefurbish) {
                    NotificationManager.warning(
                        messages['mark_as_rma_devices_is_already_refirbished'],
                        messages['mark_as_rma_title'],
                        4000,
                    );
                } else if (validateRMA) {
                    NotificationManager.warning(
                        messages['mark_as_rma_devices_is_already_rma'],
                        messages['mark_as_rma_title'],
                        4000,
                    );
                } else
                    dispatch(markAsRmaOpenModal(selectedItems));
            }
        },
        {
            id: "accounts_main_reprocess_odo_hours",
            label: messages['accounts_main_reprocess_odo_hours'],
            show: true,
            disabled: !HasPermission(user, "rprcssoh", "c"),
            multiple: true,
            action: () => {
                dispatch(reprocessOdoHoursOpenModal({ selectedAccount: company, selectedDevices: selectedItems }))
            }
        },
    ];

    /**
     * Extra filters component - Status dropdown filter
     */
    const renderExtraFilters = () => {
        return (
            <div className="extra-filters-tab">
                <Select
                    id="change-status-id"
                    valueField="id"
                    textField="label"
                    value={getStatusOptions(messages).find(x => x.id == status)}
                    placeholder={messages['account_change_status_label']}
                    options={getStatusOptions(messages) || []}
                    onChange={(status) => {
                        setStatus(status ? status.id : null);
                        let currentConditions = tableRef?.current?.conditions;
                        let newConditions = currentConditions.filter((item) => item.fields[0] !== 'status');

                        if (status) {
                            newConditions.push({ fields: ["status"], terms: [`${status.id}`], exact: true })
                        }

                        tableRef?.current?.getData({ newConditions: newConditions });
                    }}
                    isClearable
                />
            </div>
        )
    };

    // Memoized so DataViewInfiniteScroll only re-renders when warnings data or language changes
    const maintenanceWarningsColumn = useMemo(() => ({
        render: (row) => {
            const warningData = warningsMap[row.id];
            if (!warningData) return null;

            const isOffline = warningData.isOffline !== null ? warningData.isOffline : false;
            const warnings = warningData.maintenanceWarnings || 0;
            let isSdCardError = false;
            try {
                const sdCard = warningData.sdCard ? JSON.parse(warningData.sdCard) : null;
                const sdCardStatus = sdCard?.status?.toUpperCase();
                isSdCardError = sdCardStatus && sdCardStatus !== "NORMAL";
            } catch (exc) { }

            const { title, color } = getWarningCategory(isSdCardError, warnings, isOffline, messages);
            if (!title) return null;

            return (
                <div>
                    <Tooltip title={title}>
                        <Icon style={{ cursor: "pointer", color: color }} className="toolbar-list-icon fs-20" onClick={() => {
                            if (isSdCardError || warnings) {
                                dispatch(openModalMaintenanceWarnings({ ...row, ...warningData }))
                            }
                        }} >
                            warning
                        </Icon>
                    </Tooltip>
                </div>
            );
        }
    }), [warningsMap, messages]);

    const permanentConditions = useMemo(() => [
        { fields: ["companyId"], terms: [companyId], exact: true, conditionOperator: "AND" }
    ], [companyId]);

    return (
        <div className="devices-tab" style={{ marginTop: 10 }}>
            <DataViewInfiniteScroll
                ref={tableRef}
                columns={columns}
                name={`pm-devices-tab-v3`}
                entity="devices-account"
                infiniteScroll={false}
                selectionConfig={{ showCounter: false }}
                onSelectedItems={(items) => setSelectedItems(items)}
                //search
                asyncSelect
                onChangeConditions={handleChangeConditions}
                permanentConditions={permanentConditions}
                graphqlConfig={{
                    function: "searchUnits",
                    sortType: "UnitSortInput",
                    conditionsType: "UnitSearchInput",
                    customQuery: "id esn status nextScheduledAction companyId deviceModel { id name manufacturer { id name } } linkedUnitId unitConfig { hardwareVersion firmwareId } refurbishedUnit { refurbishedUnitId } timezone company { id } simCard { simProviderAccount { name simProvider { name } } iccId } linkedMode linkedByUnit { id label deviceModelId }",
                    dynamicQuery: true,
                }}
                searchConfig={{
                    conditions: ["label", "esn", "imei", "reference", "deviceModel_manufacturer_name", "deviceModel_name", "simCard_msisdn", "unitConfig_hardwareVersion"],
                    optionValue: "id",
                    optionLabel: "label",
                    custom: { fields: ["companyId"], terms: [companyId], exact: true },
                    fixBar: true,
                    fields: ["label", "esn", "imei", "reference", "deviceModel_manufacturer_name", "deviceModel_name", "simCard_msisdn", "simCard_iccId", "unitConfig_hardwareVersion", "unitConfig_billingAccount_name"],
                }}
                uiConfig={{
                    hideFilters: true,
                    hideAllInPagination: true,
                    topHeight: props?.single ? 286 : 200,
                    height: height && height + "px" || "500px",
                }}
                exportConfig={{
                    fileName: "devices"
                }}
                defaultsConfig={{
                    rowsPerPage: 50,
                    valuesPerPage: [10, 25, 50, 100]
                }}
                onChangeColumns={handleChangeColumns}
                tableButtons={["export", 'orderColumn']}
                noPDF
                options={menuOptions}
                extraFilters={renderExtraFilters}
                customColumn={maintenanceWarningsColumn}
            />

            {openModalVidFleetConfiguration && <VidfleetConfiguration bulk devices={selectedItems} from={"devices"} />}
            {configurationUploadModeOpenModal && <ConfigurationUploadMode initialDevices={selectedItems} mode={"Bulk"} />}
            {configurationHubSpotOpenModal && <ConfigurationHubSpot initialDevices={selectedItems} mode={"Bulk"} />}
            {bulkPOpenModal && <BulkPairing tableRef={tableRef} />}
            {assignFirmOpenModal && <AssignFirmware account={company} />}
            {activeSimCardOpenModal && <ActivateSimCard />}
            {bulkUnOpenModal && <BulkUnpairing tableRef={tableRef} />}
            {deviceInfoOpenModal && <VidfleetInformation />}
            {changeSimPOpenModal && <ChangeSimProvider tableRef={tableRef} />}
            {editSwapSimCardOModal && <EditSwapSimCard tableRef={tableRef} />}
            {editDeviceOpenModal && <UpdateFeature tableRef={tableRef} />}
            {changeStatusOModal && <ChangeStatus tableRef={tableRef} companyId={companyId} />}
            {sendCommandsOpenModal && !massSMS && <SendCommand massSMS={false} />}
            {cgPulsCustomerOpenModal && <ChangePulsCustomer tableRef={tableRef} />}
            {cgScriptOpenModal && <ChangeScript tableRef={tableRef} />}
            {openModalMoveToOtherAccount && <MoveToOtherAccount tableRef={tableRef} account={company} />}
            {openModalMarkAsRma && <MarkAsRma tableRef={tableRef} />}
            {deviceDetailsOpenModal && <Details lastReading />}
            {bulkAOpenModal && <BulkActions principalTableRef={tableRef} />}
            {changeTimezoneOModal && <ChangeTimezone tableRef={tableRef} />}
            {rebootDeOpenModal && <RebootDevice />}
            {openModalCoverageDetails && <CoverageDetails />}
            {openModalCreateDiagnosticDevice && <CreateDiagnosticDevice />}
            {openModalReprocessOdoHoursDevices && <ReprocessOdoHours />}
            {openModalViewMaintenanceWarnings && <MaintenanceWarnings isTab={false} tableRef={tableRef} />}
            {
                /* Video Fleet */
                openModalVidFleetVideo &&
                <VidFleetOverview />
            }
            {openModalBulkTags && <BulkTags entityName={"Unit"} tableRef={tableRef} />}
            {openModalActivities && <Activities noFilters2 noOpenSearch2 modalView noUsers height={innerHeight - 257} entity={"activitiesModalDevices"} instanceId="devices-tab-modal" />}
            {assignBillingAccountOpenModal && <AssignBillingAccount tableRef={tableRef} dealerId={company?.dealerId} />}
        </div>
    )
}
export default DevicesTab;
